import {
  API_BASE_URL,
  EMAIL_KEY,
} from 'lib/constants';
import {
  AuthenticationError,
  UploadError,
} from 'lib/errors';
import { getAuthToken } from 'lib/auth';

// API integration related to applications
class Application {
  constructor() {
    this.email = localStorage.getItem(EMAIL_KEY);
    this.headers = new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    })
  }

  setEmail = (email) => {
    this.email = email;
    localStorage.setItem(EMAIL_KEY, this.email);
  }

  // Create a new application
  create = (formData) => {
    return fetch(`${API_BASE_URL}/start/applications`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(formData),
    })
    .then(response => {
      return response.json().then(data => {
        this.setEmail(data.email)
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // get headers for operations that require authentication
  getAuthHeaders = () => {
    if (!getAuthToken()) {
      throw new AuthenticationError('Application token not found');
    }

    const authHeaders = new Headers(this.headers);
    authHeaders.append('Authorization', getAuthToken());

    return authHeaders;
  }

  // Update an existing application
  update = (formData) => {
    return fetch(`${API_BASE_URL}/start/application`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(formData),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Retrieve existing application details
  retrieve = () => {
    return fetch(`${API_BASE_URL}/start/application`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Get application metadata (fields details, dropdown options...)
  retrieveMetadata = () => {
    return fetch(`${API_BASE_URL}/start/application`, {
      method: 'OPTIONS',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Get existing Persona Inquiry details if applicable
  retrievePersonaInquiry = () => {
    return fetch(`${API_BASE_URL}/start/application/inquiry`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  upload = (document_key, file, owner_key) => {
    const baseUrl = `${API_BASE_URL}/start/application`;
    let url = `${baseUrl}/upload-url?document=${document_key}`;

    if (owner_key) {
      url = `${baseUrl}/owner/${owner_key}/upload-url?document=${document_key}`;
    }

    return fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    .then(httpResponse => {
      return httpResponse.json();
    }).then(jsonResponse => {
      if (!jsonResponse || jsonResponse.error) {
        throw new UploadError(JSON.stringify(jsonResponse));
      }

      return fetch(`${jsonResponse[document_key]}`, {
        method: 'PUT',
        body: file,
      })
    }).catch((error) => {
      throw new UploadError(error);
    })
  }

  // Submit application
  submit = () => {
    return fetch(`${API_BASE_URL}/start/application/submit`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Clear application data
  clear = () => localStorage.clear();

  /* ENDPOINTS SPECIFIC TO BENEFICIAL OWNER VERIFICATION  */
  // Update an existing owner
  updateBO = (formData) => {
    return fetch(`${API_BASE_URL}/start/application/owner`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(formData),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Retrieve existing owner details
  retrieveBO = () => {
    return fetch(`${API_BASE_URL}/start/application/owner`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }

  // Get owner metadata (fields details, dropdown options...)
  retrieveMetadataBO = () => {
    return fetch(`${API_BASE_URL}/start/application/owner`, {
      method: 'OPTIONS',
      headers: this.getAuthHeaders(),
    })
    .then(response => {
      return response.json().then(data => {
        return data;
      })
    })
    .catch((error) => {
      throw new Error(error);
    })
  }
}

const application = new Application();
export default application;
export { Application };
